<template>
  <Header />
  <SideBar />
  <main class="ml-64 min-h-screen bg-gray-50">
    <div class="p-8">
      <!-- Page Header -->
      <div class="flex justify-between items-center mb-8">
        <div>
          <h1 class="text-2xl font-bold text-green-900">Top-Up Requests</h1>
          <p class="mt-1 text-sm text-gray-500">Manage organisation account top-up requests</p>
        </div>
        <button v-if="authStore.hasPermissions('create_topup_requests', 'local')" @click="showCreateModal = true" class="bg-green-800 hover:bg-green-700 text-white px-5 py-2.5 rounded-md flex items-center
          space-x-2 font-medium bg-gradient-to-r from-green-700 to-green-800 focus:outline-none
          focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200
          transform hover:scale-[1.02]">
          <i class="fa-solid fa-plus mr-1"></i>
          Create Top-up Request
        </button>
      </div>

      <!-- Table -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200">
        <div v-if="billingStore.loading" class="flex justify-center items-center h-48">
          <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
        </div>

        <div v-else-if="billingStore.topUpRequests.length === 0" class="p-12 text-center text-gray-400">
          <i class="fa-solid fa-inbox text-4xl mb-3"></i>
          <p class="text-sm">No top-up requests found</p>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organization
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Narration</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th v-if="canApprove"
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="req in billingStore.topUpRequests" :key="req.id" class="hover:bg-gray-50 transition-colors">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">{{ req.name }}</div>
                  <div class="text-xs text-gray-400">{{ req.contactEmail }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  UGX {{ req.amount }}
                </td>
                <td class="px-6 py-4 text-sm text-gray-600 max-w-xs">
                  <span :title="req.narration" class="block truncate max-w-xs">{{ req.narration || '—' }}</span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="statusBadgeClass(req.status)">{{ req.status }}</span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {{ formatDate(req.createdAt) }}
                </td>
                <td v-if="canApprove" class="px-6 py-4 whitespace-nowrap">
                  <div v-if="req.status.toLowerCase() === 'pending'" class="flex items-center space-x-2">
                    <button @click="openApprovalModal(req, 'APPROVED')" class="inline-flex items-center px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50
                      border border-green-200 rounded-md hover:bg-green-100 transition-colors">
                      <i class="fa-solid fa-check mr-1.5"></i>Approve
                    </button>
                    <button @click="openApprovalModal(req, 'REJECTED')" class="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50
                      border border-red-200 rounded-md hover:bg-red-100 transition-colors">
                      <i class="fa-solid fa-xmark mr-1.5"></i>Reject
                    </button>
                  </div>
                  <span v-else class="text-xs text-gray-400 italic">No action needed</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="billingStore.pagination.totalPages > 1" class="px-6 py-4 border-t border-gray-200">
          <div class="flex items-center justify-between">
            <div class="text-sm text-gray-500">
              Page <span class="font-medium text-gray-700">{{ currentPage }}</span>
              of <span class="font-medium text-gray-700">{{ billingStore.pagination.totalPages }}</span>
            </div>
            <div class="flex space-x-2">
              <button :disabled="currentPage <= 1" @click="changePage(currentPage - 1)" class="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50
                disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                <i class="fa-solid fa-chevron-left mr-1"></i>Previous
              </button>
              <button :disabled="currentPage >= billingStore.pagination.totalPages" @click="changePage(currentPage + 1)"
                class="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50
                disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                Next<i class="fa-solid fa-chevron-right ml-1"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create Top-up Modal -->
    <Modal :show="showCreateModal" title="Create Top-up Request" size="max-w-lg" @close="closeCreateModal">
      <div v-if="createLoading" class="flex justify-center items-center h-32">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
      </div>
      <form v-else class="space-y-5" @submit.prevent="handleCreateTopUp">
        <div class="space-y-1">
          <label class="block text-sm font-medium text-gray-700">Organization</label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <i class="fa-solid fa-building text-gray-400 text-sm"></i>
            </div>
            <select v-model="topUpForm.organizationId" required class="block w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-md text-sm text-gray-700
              focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white">
              <option value="">Select organisation</option>
              <option v-for="org in organizationStore.organizationResults" :key="org.id" :value="org.id">
                {{ org.name }}
              </option>
            </select>
          </div>
          <p class="text-xs text-gray-400">Select the organisation to top up</p>
        </div>

        <div class="space-y-1">
          <label class="block text-sm font-medium text-gray-700">Amount (UGX)</label>
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <i class="fa-solid fa-coins text-gray-400 text-sm"></i>
            </div>
            <input v-model="topUpForm.amount" type="number" min="1" required class="block w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-md text-sm text-gray-700
              placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="e.g. 50000" />
          </div>
          <p class="text-xs text-gray-400">Amount to credit to the organisation account</p>
        </div>

        <div class="space-y-1">
          <label class="block text-sm font-medium text-gray-700">Narration</label>
          <div class="relative">
            <div class="absolute top-2.5 left-3 pointer-events-none">
              <i class="fa-solid fa-comment-lines text-gray-400 text-sm"></i>
            </div>
            <textarea v-model="topUpForm.narration" rows="3" required
              class="block w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-md text-sm text-gray-700
              placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none" placeholder="Reason for this top-up request" />
          </div>
          <p class="text-xs text-gray-400">Brief description for this request</p>
        </div>

        <div
          class="bg-amber-50 border border-amber-200 rounded-md px-4 py-3 flex items-start space-x-2 text-xs text-amber-800">
          <i class="fa-solid fa-triangle-exclamation mt-0.5 flex-shrink-0"></i>
          <span>This request will require approval before funds are credited to the organisation.</span>
        </div>

        <div class="flex justify-end space-x-2 pt-1">
          <button type="button" @click="closeCreateModal"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" class="px-5 py-2 text-sm font-medium text-white bg-green-800 rounded-md hover:bg-green-700
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors">
            Submit Request
          </button>
        </div>
      </form>
    </Modal>

    <!-- Approve / Reject Confirmation Modal -->
    <Modal :show="showApprovalModal" :title="approvalAction === 'APPROVED' ? 'Approve Request' : 'Reject Request'"
      size="max-w-md" @close="closeApprovalModal">
      <div v-if="approvalLoading" class="flex justify-center items-center h-24">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
      </div>
      <div v-else class="space-y-4">
        <div :class="approvalAction === 'APPROVED'
          ? 'bg-green-50 border border-green-200 rounded-md p-4'
          : 'bg-red-50 border border-red-200 rounded-md p-4'">
          <div class="flex items-start space-x-3">
            <i :class="approvalAction === 'APPROVED'
              ? 'fa-solid fa-circle-check text-green-600 mt-0.5'
              : 'fa-solid fa-circle-xmark text-red-600 mt-0.5'"></i>
            <div class="text-sm">
              <p :class="approvalAction === 'APPROVED' ? 'font-medium text-green-800' : 'font-medium text-red-800'">
                {{ approvalAction === 'APPROVED' ? 'Approve this request?' : 'Reject this request?' }}
              </p>
              <p v-if="pendingRequest"
                :class="approvalAction === 'APPROVED' ? 'text-green-700 mt-1' : 'text-red-700 mt-1'">
                <span class="font-medium">{{ pendingRequest.name }}</span> — UGX {{ pendingRequest.amount }}
              </p>
            </div>
          </div>
        </div>

        <p class="text-sm text-gray-600">
          {{ approvalAction === 'APPROVED'
            ? 'Approving will credit the specified amount to the organisation account immediately.'
            : 'Rejecting will close this request. The organisation account will not be credited.' }}
        </p>

        <div class="flex justify-end space-x-2 pt-1">
          <button type="button" @click="closeApprovalModal"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
            Cancel
          </button>
          <button @click="confirmApproval"
            :class="approvalAction === 'APPROVED'
              ? 'px-5 py-2 text-sm font-medium text-white bg-green-700 rounded-md hover:bg-green-600 focus:ring-green-500 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors'
              : 'px-5 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-500 focus:ring-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors'">
            {{ approvalAction === 'APPROVED' ? 'Yes, Approve' : 'Yes, Reject' }}
          </button>
        </div>
      </div>
    </Modal>
  </main>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue';
import { useBillingStore } from '@/store/billing';
import { useOrganizationStore } from '@/store/organizations';
import { useNotificationStore } from '@/store/notifications';
import { useAuthStore } from '@/store/auth';
import type { TopUpRequest } from '@/config/types/responses';
import Modal from '@/components/modal.vue';
import SideBar from '../components/sidebar.vue';
import Header from '../components/header.vue';

const billingStore = useBillingStore();
const organizationStore = useOrganizationStore();
const notificationStore = useNotificationStore();
const authStore = useAuthStore();

const currentPage = ref(1);
const showCreateModal = ref(false);
const createLoading = ref(false);
const showApprovalModal = ref(false);
const approvalLoading = ref(false);
const approvalAction = ref<'APPROVED' | 'REJECTED'>('APPROVED');
const pendingRequest = ref<TopUpRequest | null>(null);

const topUpForm = ref({
  organizationId: '',
  amount: 0,
  narration: '',
});

const canApprove = computed(() => {
  const purpose = localStorage.getItem('purpose');
  if (purpose !== 'local') return false;
  const permissions = localStorage.getItem('permissions');
  if (!permissions) return false;
  return permissions.split(',').map((p) => p.trim().toLowerCase()).includes('approve_topup_requests');
});

onMounted(() => {
  billingStore.fetchTopUpRequests(currentPage.value);
  organizationStore.fetchOrganizations(1);
});

function changePage(page: number) {
  currentPage.value = page;
  billingStore.fetchTopUpRequests(page);
}

function formatDate(input: string) {
  if (!input) return '—';
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: '2-digit', year: 'numeric' }).format(date);
}

function statusBadgeClass(status: string) {
  switch (status) {
    case 'APPROVED': return 'px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700';
    case 'REJECTED': return 'px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-700';
    case 'PENDING': return 'px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-700';
    default: return 'px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600';
  }
}

function closeCreateModal() {
  showCreateModal.value = false;
  topUpForm.value = { organizationId: '', amount: 0, narration: '' };
  billingStore.clearMessages();
}

async function handleCreateTopUp() {
  createLoading.value = true;
  const success = await billingStore.createTopUpRequest(
    topUpForm.value.organizationId,
    topUpForm.value.amount,
    topUpForm.value.narration,
  );
  createLoading.value = false;

  if (success) {
    notificationStore.showSuccess('Request Created', 'Top-up request submitted successfully.');
    closeCreateModal();
    billingStore.fetchTopUpRequests(currentPage.value);
  } else {
    notificationStore.showError('Request Failed', billingStore.error || 'Failed to create top-up request.');
  }
}

function openApprovalModal(req: TopUpRequest, action: 'APPROVED' | 'REJECTED') {
  pendingRequest.value = req;
  approvalAction.value = action;
  showApprovalModal.value = true;
}

function closeApprovalModal() {
  showApprovalModal.value = false;
  pendingRequest.value = null;
}

async function confirmApproval() {
  if (!pendingRequest.value) return;
  approvalLoading.value = true;
  const success = await billingStore.updateTopUpRequest(pendingRequest.value.id, approvalAction.value);
  approvalLoading.value = false;

  if (success) {
    const label = approvalAction.value === 'APPROVED' ? 'Approved' : 'Rejected';
    notificationStore.showSuccess(`Request ${label}`, `The top-up request has been ${approvalAction.value}.`);
    closeApprovalModal();
    billingStore.fetchTopUpRequests(currentPage.value);
  } else {
    notificationStore.showError('Action Failed', billingStore.error || 'Failed to update request.');
  }
}
</script>
